/**
 * Invoice API Service
 *
 * Handles all API calls for workshop invoices:
 * - CRUD operations
 * - Payment recording
 * - Invoice lifecycle (send, void)
 * - Search and filtering
 * - Line item management (add adjustments, edit, delete)
 * - PDF download
 * - Backend search by customer / vehicle / date range
 * - Invoice archive (reconciled invoices)
 */

import axiosInstance from './axiosInstance';

// ============================================
// CRUD Operations
// ============================================

export const getInvoicesApi = async ({ page = 0, size = 20 } = {}) => {
    const response = await axiosInstance.get('/invoices', {
        params: { page, size }
    });
    return response.data;
};

export const getInvoiceApi = async (uuid) => {
    const response = await axiosInstance.get(`/invoices/${uuid}`);
    return response.data;
};

export const getInvoiceByBookingApi = async (bookingUuid) => {
    const response = await axiosInstance.get(`/invoices/by-booking/${bookingUuid}`);
    return response.data;
};

export const createInvoiceApi = async (data) => {
    const response = await axiosInstance.post('/invoices', data);
    return response.data;
};

export const updateInvoiceApi = async (uuid, data) => {
    const response = await axiosInstance.put(`/invoices/${uuid}`, data);
    return response.data;
};

// ============================================
// Invoice Lifecycle
// ============================================

export const sendInvoiceApi = async (uuid, data = {}) => {
    // Always send an explicit JSON body so Micronaut's @Body binding
    // never sees an empty request and returns 400 "Required Body not specified".
    const response = await axiosInstance.post(`/invoices/${uuid}/send`, data, {
        headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
};

export const voidInvoiceApi = async (uuid, reason = null) => {
    const response = await axiosInstance.post(`/invoices/${uuid}/void`, { reason }, {
        headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
};

/**
 * Manually reconcile a PAID invoice.
 * Creates an archive entry and transitions to RECONCILED status.
 */
export const reconcileInvoiceApi = async (uuid) => {
    const response = await axiosInstance.post(`/invoices/${uuid}/reconcile`, {}, {
        headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
};

/**
 * Reconcile several invoices in one request.
 *
 * PARTIAL SUCCESS: a 200 does NOT mean everything worked. The response reports the outcome
 * of every invoice — read `failed` and `results` before telling the user it is done. One
 * stale invoice must not throw away the rest of the batch, and staff must not be left
 * guessing which ones landed.
 *
 * @param {string[]} invoiceUuids max 200 — the server rejects more rather than quietly
 *        running for minutes on a "select all".
 * @returns {Promise<{requested: number, succeeded: number, failed: number,
 *   results: {invoiceUuid: string, invoiceNumber: string|null,
 *             success: boolean, error: string|null}[]}>}
 */
export const reconcileInvoicesBatchApi = async (invoiceUuids) => {
    const response = await axiosInstance.post('/invoices/reconcile-batch', { invoiceUuids });
    return response.data;
};

// ============================================
// PDF Operations
// ============================================

/**
 * Download the invoice PDF.
 * - For SENT/PAID invoices: serves the stored S3 PDF.
 * - For DRAFT invoices: generates a live preview on the fly.
 *
 * Returns a Blob so the caller can create an object URL for rendering
 * inline in the browser or trigger a save-as download.
 */
export const downloadInvoicePdfApi = async (uuid) => {
    const response = await axiosInstance.get(`/invoices/${uuid}/pdf`, {
        responseType: 'blob',
    });
    return response.data; // Blob
};

/**
 * Opens the invoice PDF in a new browser tab.
 * Uses a temporary object URL so no token is exposed in the URL bar.
 */
export const openInvoicePdfInTab = async (uuid) => {
    const blob = await downloadInvoicePdfApi(uuid);
    const url = URL.createObjectURL(blob);
    const tab = window.open(url, '_blank');
    // Revoke after the new tab has had time to load
    if (tab) {
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }
    return url;
};

/**
 * Triggers a browser Save-As download of the invoice PDF.
 */
export const saveInvoicePdf = async (uuid, filename) => {
    const blob = await downloadInvoicePdfApi(uuid);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `invoice-${uuid}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// ============================================
// Payment Operations
// ============================================

export const recordPaymentApi = async (uuid, data) => {
    const response = await axiosInstance.post(`/invoices/${uuid}/payments`, data);
    return response.data;
};

// ============================================
// Line Item Operations
// ============================================

/**
 * Fetches the available line item types for the "Add Line Item" dropdown.
 * Sourced from the backend QuoteLineItem.LineType enum so the UI never drifts
 * out of sync with the server.
 */
export const getLineItemTypesApi = async () => {
    const response = await axiosInstance.get('/invoices/line-item-types');
    return response.data;
};

/**
 * Fetches the available payment methods for the Record Payment dialog dropdown.
 * Sourced from the backend Invoice.PaymentMethod enum so the UI never drifts
 * out of sync with the server.
 */
export const getPaymentMethodsApi = async () => {
    const response = await axiosInstance.get('/invoices/payment-methods');
    return response.data;
};

export const addLineItemApi = async (invoiceUuid, data) => {
    const response = await axiosInstance.post(`/invoices/${invoiceUuid}/line-items`, data);
    return response.data;
};

export const updateLineItemApi = async (invoiceUuid, lineItemUuid, data) => {
    const response = await axiosInstance.put(`/invoices/${invoiceUuid}/line-items/${lineItemUuid}`, data);
    return response.data;
};

export const deleteLineItemApi = async (invoiceUuid, lineItemUuid) => {
    const response = await axiosInstance.delete(`/invoices/${invoiceUuid}/line-items/${lineItemUuid}`);
    return response.data;
};

// ============================================
// Filter and Search
// ============================================

export const getInvoicesByStatusApi = async (status, { page = 0, size = 20 } = {}) => {
    const response = await axiosInstance.get(`/invoices/status/${status}`, {
        params: { page, size }
    });
    return response.data;
};

/**
 * List outstanding invoices — both PARTIALLY_PAID and TRADE_CREDIT in one page.
 * Backs the "Partial" tab which surfaces all invoices with money still to collect.
 */
export const getOutstandingInvoicesApi = async ({ page = 0, size = 20 } = {}) => {
    const response = await axiosInstance.get('/invoices/outstanding', {
        params: { page, size }
    });
    return response.data;
};

/**
 * General keyword search — searches invoice number, customer name, booking code.
 * Used by the top-bar search box.
 */
export const searchInvoicesApi = async (query, { page = 0, size = 20 } = {}) => {
    const response = await axiosInstance.get('/invoices/search', {
        params: { q: query, page, size }
    });
    return response.data;
};

/**
 * Advanced backend search — filter by customer name/phone, vehicle rego,
 * and/or issue date range. Results are paginated.
 *
 * All parameters are optional; at least one should be provided.
 *
 * @param {object} filters
 * @param {string}  [filters.customerQuery]  - customer name or phone
 * @param {string}  [filters.vehicleRego]    - vehicle registration number
 * @param {string}  [filters.dateFrom]       - ISO date string (inclusive)
 * @param {string}  [filters.dateTo]         - ISO date string (inclusive)
 * @param {number}  [filters.page]
 * @param {number}  [filters.size]
 */
/**
 * The Invoices page's search.
 *
 * 🔑 `dateField` says which date the From/To window filters — 'ISSUE' (when the invoice
 * was raised) or 'DUE'. One at a time: an invoice raised in March and due in April is
 * inside an April window on one reading and outside it on the other.
 *
 * ⚠️ `status` is sent so the tab NARROWS the search. Searching from the Sent tab used to
 * run across every status and then be shown under a heading saying "no sent invoices
 * found", which reads as "this customer has none" rather than "the filter was ignored".
 *
 * ⚠️ dateFrom/dateTo were already being sent here and accepted by NOTHING — the comment
 * this replaces said the backend "should support" them. The date boxes did nothing
 * whenever a search was running.
 */
export const advancedSearchInvoicesApi = async ({
                                                    customerQuery = '',
                                                    vehicleRego = '',
                                                    dateFrom = '',
                                                    dateTo = '',
                                                    dateField = 'ISSUE',
                                                    status = '',
                                                    page = 0,
                                                    size = 20,
                                                } = {}) => {
    // 🪤 NOT joined with a space. "Instyle PTY.LTD" plus a rego became one term that
    // matched neither, so filling in both boxes guaranteed no results.
    const q = (customerQuery || vehicleRego || '').trim();

    const params = { page, size, dateField };
    if (q) params.q = q;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (status && status !== 'all') params.status = status;

    const response = await axiosInstance.get('/invoices/search', { params });
    return response.data;
};

/**
 * Raise a job for an invoice that has none, on a nominated vehicle.
 *
 * 🔑 Every migrated invoice has no job — the import creates none on purpose — so an
 * imported invoice records what was charged and nothing about the work. This turns the
 * charge back into a job, each priced line becoming a work item.
 *
 * ⚠️ vehicleUuid is required: job.vehicle_id is NOT NULL and an imported invoice knows
 * its customer but not their car. Nominating one is also what finally fills the empty
 * VEHICLE box, which reads the vehicle back through the job.
 */
export const createJobForInvoiceApi = async (invoiceUuid, vehicleUuid) => {
    const response = await axiosInstance.post(
        `/invoices/${invoiceUuid}/create-job`, { vehicleUuid });
    return response.data;
};

export const getOverdueInvoicesApi = async () => {
    const response = await axiosInstance.get('/invoices/overdue');
    return response.data;
};

// ============================================
// Invoice Archive (Reconciled Invoices)
// ============================================

/**
 * List all archived (reconciled) invoices, newest sync first.
 * Supports optional date range filtering on syncedAt.
 *
 * @param {object}  opts
 * @param {number}  [opts.page]
 * @param {number}  [opts.size]
 * @param {string}  [opts.dateFrom]  - ISO date string (inclusive)
 * @param {string}  [opts.dateTo]    - ISO date string (inclusive)
 */
export const getInvoiceArchiveApi = async ({ page = 0, size = 20, dateFrom = '', dateTo = '' } = {}) => {
    const params = { page, size };
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    const response = await axiosInstance.get('/invoice-archive', { params });
    return response.data;
};

/**
 * Search archived invoices by invoice number or job number,
 * with optional date range filtering on syncedAt.
 *
 * @param {string}  query
 * @param {object}  opts
 * @param {number}  [opts.page]
 * @param {number}  [opts.size]
 * @param {string}  [opts.dateFrom]  - ISO date string (inclusive)
 * @param {string}  [opts.dateTo]    - ISO date string (inclusive)
 */
export const searchInvoiceArchiveApi = async (query, { page = 0, size = 20, dateFrom = '', dateTo = '' } = {}) => {
    const params = { q: query, page, size };
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;

    const response = await axiosInstance.get('/invoice-archive/search', { params });
    return response.data;
};

// ============================================
// Default Export
// ============================================

// ============================================
// Income & Sales Targets
// ============================================

/**
 * Annual invoice totals for tax, with a month-by-month breakdown.
 *
 * Every figure — ex-GST, GST, inc-GST, collected, outstanding, target variance,
 * percentages — arrives pre-computed. Nothing here re-derives a number the workshop
 * makes decisions on; that arithmetic lives in the backend where it is unit tested.
 *
 * @param year  reporting year. Omit to let the server pick the current one for the basis.
 * @param basis 'FINANCIAL' (AU Jul-Jun, the default) or 'CALENDAR'.
 */
export const getAnnualIncomeApi = async ({ year, basis = 'FINANCIAL' } = {}) => {
    const response = await axiosInstance.get('/invoices/income/annual', {
        params: { ...(year ? { year } : {}), basis },
    });
    return response.data;
};

/**
 * One month's invoiced income against its sales target, including pace
 * (pro-rata target, on-track, projected month-end) while the month is still running.
 */
export const getMonthlyIncomeApi = async ({ year, month } = {}) => {
    const response = await axiosInstance.get('/invoices/income/monthly', {
        params: { ...(year ? { year } : {}), ...(month ? { month } : {}) },
    });
    return response.data;
};

/**
 * One month expanded into weeks, for the chart's Weekly view.
 *
 * ⚠️ Absent params must be ABSENT, not empty — `?year=` is a parse failure in Micronaut
 * and 400s the whole request rather than falling back to the current month.
 */
export const getWeeklyIncomeApi = async ({ year, month } = {}) => {
    const response = await axiosInstance.get('/invoices/income/weekly', {
        params: { ...(year ? { year } : {}), ...(month ? { month } : {}) },
    });
    return response.data;
};

/** Years this workshop has invoices for, so the year picker offers real options. */
export const getIncomeYearsApi = async () => {
    const response = await axiosInstance.get('/invoices/income/years');
    return response.data;
};

/** Targets already set for a calendar year. */
export const getSalesTargetsApi = async (year) => {
    const response = await axiosInstance.get('/invoices/income/targets', {
        params: { ...(year ? { year } : {}) },
    });
    return response.data;
};

/**
 * Set a month's sales target.
 *
 * PUT with a JSON body, never a POST carrying query params: Micronaut rejects a
 * body-less POST that reads @QueryValue with a 415 before the handler runs, which
 * presents as every call failing with nothing in the server log.
 *
 * @param applyToRemainingMonths also seed later months of the same year that have no
 *                               target yet. Months already set are left untouched.
 */
export const setSalesTargetApi = async ({ year, month, targetAmount, applyToRemainingMonths = false }) => {
    const response = await axiosInstance.put(
        '/invoices/income/targets',
        { year, month, targetAmount, applyToRemainingMonths },
        { headers: { 'Content-Type': 'application/json' } },
    );
    return response.data;
};

/** Remove a month's target, returning it to "no target" — not a target of zero. */
export const clearSalesTargetApi = async ({ year, month }) => {
    const response = await axiosInstance.delete(`/invoices/income/targets/${year}/${month}`);
    return response.data;
};

const invoiceApi = {
    // CRUD
    getInvoicesApi,
    getInvoiceApi,
    getInvoiceByBookingApi,
    createInvoiceApi,
    updateInvoiceApi,
    // Lifecycle
    sendInvoiceApi,
    voidInvoiceApi,
    reconcileInvoiceApi,
    reconcileInvoicesBatchApi,
    // PDF
    downloadInvoicePdfApi,
    openInvoicePdfInTab,
    saveInvoicePdf,
    // Payments
    recordPaymentApi,
    // Line Items
    addLineItemApi,
    updateLineItemApi,
    deleteLineItemApi,
    getLineItemTypesApi,
    getPaymentMethodsApi,
    // Filter & Search
    getInvoicesByStatusApi,
    getOutstandingInvoicesApi,
    searchInvoicesApi,
    advancedSearchInvoicesApi,
    getOverdueInvoicesApi,
    // Archive
    getInvoiceArchiveApi,
    searchInvoiceArchiveApi,
    // Income & sales targets
    getAnnualIncomeApi,
    getMonthlyIncomeApi,
    getIncomeYearsApi,
    getSalesTargetsApi,
    setSalesTargetApi,
    clearSalesTargetApi,
};

export default invoiceApi;
